import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, CalendarDays, CheckCircle2, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { getRatgeberArticle, getRelatedRatgeberArticles } from "../data/ratgeberArticles";
import {
  getArticleGuidanceLinks,
  getArticleSeoGuidance,
  getGuidanceCluster,
  getGuidanceLastReviewedAt,
} from "../data/articleSeoGuidance";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { getServiceLinksForArticle } from "../data/seoTopicClusters";

const formatDate = (date) =>
  date
    ? new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(`${date}T00:00:00`))
    : null;

export const RatgeberArticle = () => {
  const { slug } = useParams();
  const article = getRatgeberArticle(slug);

  if (!article) {
    return <Navigate to="/ratgeber" replace />;
  }

  const relatedArticles = getRelatedRatgeberArticles(article);
  const serviceLinks = getServiceLinksForArticle(article.slug);
  const seoGuidance = getArticleSeoGuidance(article.slug);
  const guidanceLinks = getArticleGuidanceLinks(article.slug);
  const guidanceCluster = getGuidanceCluster(article.slug);
  const reviewedAt = formatDate(getGuidanceLastReviewedAt(article));

  return (
    <article className="bg-white" data-testid="ratgeber-article-page">
      <section className="bg-[#122A5C] pt-28 pb-12 text-white md:pt-36 md:pb-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/ratgeber"
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-white/70 transition-colors hover:text-[#F28A0D]"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zum Ratgeber
          </Link>

          <div className="mb-5 flex flex-wrap items-center gap-4 text-sm text-white/70">
            <span className="inline-flex items-center gap-2 font-semibold text-[#F28A0D]">
              <BookOpen className="h-4 w-4" />
              {article.category}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {article.readingTime}
            </span>
            {reviewedAt ? (
              <span className="inline-flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                Aktualisiert am {reviewedAt}
              </span>
            ) : null}
          </div>

          <h1 className="font-heading mb-6 text-4xl font-bold leading-tight md:text-5xl">
            {article.title}
          </h1>
          <p className="text-lg leading-relaxed text-white/75 md:text-xl">
            {article.excerpt}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="-mt-8 overflow-hidden rounded-lg shadow-2xl md:-mt-12">
          <ResponsiveImage
            src={article.image}
            alt={article.imageAlt}
            width={article.imageWidth}
            height={article.imageHeight}
            sizes="(min-width: 1024px) 896px, 100vw"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="h-[260px] w-full object-cover md:h-[420px]"
          />
        </div>
      </div>

      <section className="py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:px-8">
          <div className="mx-auto max-w-3xl lg:mx-0">
            <div className="space-y-12">
              {article.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading mb-4 text-2xl font-bold leading-tight text-[#122A5C] md:text-3xl">
                    {section.heading}
                  </h2>
                  <div className="space-y-4 text-lg leading-relaxed text-slate-700">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                  {section.bullets ? (
                    <ul className="mt-6 space-y-3">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-3 text-lg text-slate-700">
                          <span className="mt-3 h-2 w-2 flex-shrink-0 rounded-full bg-[#E07000]" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ))}
            </div>

            {seoGuidance ? (
              <section className="mt-14 rounded-lg border border-[#F28A0D]/25 bg-[#FFF7ED] p-6 md:p-8">
                <div className="mb-5">
                  <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#E07000]">
                    Kurzantwort
                  </p>
                  <h2 className="font-heading text-2xl font-bold leading-tight text-[#122A5C] md:text-3xl">
                    Kurz eingeordnet
                  </h2>
                </div>
                <p className="text-lg leading-relaxed text-slate-700">
                  {seoGuidance.quickAnswer}
                </p>

                {seoGuidance.decisionPoints?.length ? (
                  <div className="mt-7">
                    <h3 className="font-heading mb-4 text-xl font-bold text-[#122A5C]">
                      Wichtig für die Prüfung
                    </h3>
                    <ul className="space-y-3">
                      {seoGuidance.decisionPoints.map((point) => (
                        <li key={point} className="flex gap-3 text-slate-700">
                          <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-[#E07000]" />
                          <span className="leading-relaxed">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {guidanceLinks.length ? (
                  <div className="mt-7 border-t border-[#F28A0D]/20 pt-6">
                    <h3 className="font-heading mb-4 text-xl font-bold text-[#122A5C]">
                      Vertiefende Ratgeber
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {guidanceLinks.map((linkedArticle) => (
                        <Link
                          key={linkedArticle.slug}
                          to={`/ratgeber/${linkedArticle.slug}`}
                          className="group rounded-lg border border-white/70 bg-white p-4 shadow-sm transition-colors hover:border-[#E07000]"
                        >
                          <span className="mb-1 block text-sm font-semibold text-[#E07000]">
                            {linkedArticle.category}
                          </span>
                          <span className="font-heading font-bold leading-snug text-[#122A5C] transition-colors group-hover:text-[#E07000]">
                            {linkedArticle.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}

            {article.faqs?.length ? (
              <section className="mt-14 rounded-lg border border-slate-200 bg-white p-6 shadow-sm md:p-8">
                <h2 className="font-heading mb-6 text-2xl font-bold text-[#122A5C]">
                  Häufige Fragen
                </h2>
                <div className="space-y-6">
                  {article.faqs.map((faq) => (
                    <div key={faq.question}>
                      <h3 className="font-heading mb-2 text-lg font-bold text-[#122A5C]">
                        {faq.question}
                      </h3>
                      <p className="leading-relaxed text-slate-700">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="mt-14 rounded-lg bg-slate-50 p-6 md:p-8">
              <h2 className="font-heading mb-3 text-2xl font-bold text-[#122A5C]">
                Fläche prüfen lassen
              </h2>
              <p className="mb-6 leading-relaxed text-slate-600">
                Sie möchten wissen, ob Ihre Fläche für ein Energieprojekt infrage
                kommt? EN:POWER prüft die wichtigsten Eckdaten und meldet sich
                mit einer ersten Einschätzung.
              </p>
              <Link to="/angebot-anfordern">
                <Button className="rounded-full bg-[#E07000] px-7 py-5 font-semibold text-white hover:bg-[#C56200]">
                  Anfrage starten
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="mb-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="font-heading mb-3 text-xl font-bold text-[#122A5C]">
                Fachliche Einordnung
              </h2>
              <p className="text-sm leading-relaxed text-slate-600">
                EN:POWER bewertet Dach-, Flächen- und Speicherstandorte projektbezogen.
                Die Inhalte dienen der ersten Orientierung und ersetzen keine technische
                Standortprüfung.
              </p>
              {guidanceCluster ? (
                <p className="mt-4 text-sm font-semibold text-[#E07000]">
                  Schwerpunkt: {guidanceCluster.serviceTitle}
                </p>
              ) : null}
              {reviewedAt ? (
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Geprüft am {reviewedAt}
                </p>
              ) : null}
            </div>
            {serviceLinks.length ? (
              <div className="mb-6 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="font-heading mb-5 text-xl font-bold text-[#122A5C]">
                  Passende Leistungen
                </h2>
                <div className="space-y-4">
                  {serviceLinks.map((service) => (
                    <Link
                      key={service.id}
                      to={service.path}
                      className="group block border-b border-slate-100 pb-4 last:border-b-0 last:pb-0"
                    >
                      <span className="mb-1 block text-sm font-semibold text-[#E07000]">
                        {service.leadKeyword}
                      </span>
                      <span className="font-heading font-bold leading-snug text-[#122A5C] transition-colors group-hover:text-[#E07000]">
                        {service.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="font-heading mb-5 text-xl font-bold text-[#122A5C]">
                Weitere Themen
              </h2>
              <div className="space-y-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/ratgeber/${related.slug}`}
                    className="group block border-b border-slate-100 pb-5 last:border-b-0 last:pb-0"
                  >
                    <span className="mb-2 block text-sm font-semibold text-[#E07000]">
                      {related.category}
                    </span>
                    <span className="font-heading font-bold leading-snug text-[#122A5C] transition-colors group-hover:text-[#E07000]">
                      {related.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </article>
  );
};
